import React, { useState } from 'react';
import { executeWorkflowTransition } from '../../services/workflowService';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import CustomTooltip from '../reusable/CustomTooltip';
import CustomConfirmationModal from '../reusable/CustomConfirmationModal';
import { IconButton } from '@mui/material';
import InfoIcon from '../assets/info-icon.svg';
import './LockDataUploadButton.css';

interface LockDataUploadButtonProps {
  onLockSuccess: (transitionResponse: any) => void;
}

const LockDataUploadButton: React.FC<LockDataUploadButtonProps> = ({ onLockSuccess }) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen } = useErrorModal();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const handleLockData = async () => {
    if (!userDetails || !userDetails.eoaId || !userDetails.access_token) return;

    try {
      setIsLoading(true);
      const transitionResponse = await executeWorkflowTransition(
        userDetails.eoaId,
        userDetails.access_token,
        'mark_data_upload_done'
      );
      onLockSuccess(transitionResponse);
    } catch (error) {
      handleOpen('Error occurred while locking data upload', error as Error);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleConfirm = () => {
    handleLockData();
  };

  const handleCancel = () => {
    setShowConfirmation(false);
  };

  const handleShowConfirmation = () => {
    setShowConfirmation(true);
  };

  return (
    <>
      <div className="lock-data-upload-container">
        <CustomButton
          variant="primary"
          buttonText="Lock data upload"
          buttonAction={handleShowConfirmation}
          loading={isLoading}
        />
        <CustomTooltip
          title="An XQ Admin can use the 'Lock Data' button to indicate that data is ready for internal quality review."
          placement="top"
          arrow={true}
        >
          <IconButton className="info-icon" sx={{ '&:hover': { backgroundColor: 'var(--background-white)' } }}>
            <img className="icon-size" src={InfoIcon} alt="Info" />
          </IconButton>
        </CustomTooltip>
      </div>

      <CustomConfirmationModal
        isOpen={showConfirmation}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        title="Are you sure?"
        message="By confirming, you are locking the data upload page for the district."
        confirmText="Confirm"
        cancelText="Cancel"
        isLoading={isLoading}
      />
    </>
  );
};

export default LockDataUploadButton;
